import {ApiResponse} from '../models/ApiResponse';
import {apiClient} from './baseApiClient';
import type {
  Attestation,
  Citation,
  BrandColorRole,
  JobSummary,
  JobView,
  LibraryVoice,
  Publication,
  Question,
  ReelMotionUpdate,
  StudioCapacity,
} from '../models/Studio/StudioModels';

/*
 * Microreel Studio (Tapybl.Studio), served by the platform API under /studio/{organizationId}.
 * Requests go through the platform client, which sends the session cookies and refreshes an
 * expired session. To develop against a local API, point REACT_APP_API_URL at it in .env.local
 * (or use CRA's proxy); Studio has no backend setting of its own.
 */

/**
 * What a failed request carries in `errors`: Studio's own answer (`{error}`, and flags such as
 * `unavailable` or `reconciliationRequired`), the platform's Errors list, or the client's
 * 'unknown' / 'timeout' when the server sent nothing.
 */
export type StudioFailureDetail =
  | string
  | {
      error?: unknown;
      message?: unknown;
      /** Names the refusal, e.g. "insufficient_credits". */
      code?: unknown;
      unavailable?: unknown;
      reconciliationRequired?: unknown;
    }
  | null;

/** The platform client resolves a failed request to this instead of throwing. */
export interface StudioApiResponse<T> extends Omit<ApiResponse<T>, 'errors'> {
  /** A failure's HTTP status; 0 when the request never reached the server. */
  status?: number;
  errors?: StudioFailureDetail[];
}

export interface StudioRequestOptions {
  /** Abandons the request, for example when the page that sent it closes. */
  signal?: AbortSignal;
}

/**
 * The job's storeVersion a change was made against, sent as If-Match so a change made in
 * another session is refused (409) instead of overwritten. null or undefined sends none.
 */
export type StudioVersion = number | null | undefined;

export interface SceneEdit {
  narration?: string;
  onScreen?: string;
  emphasis?: string;
  layout?: string;
  items?: unknown[];
  cites?: unknown[];
  camera?: string;
  transition?: string;
  imagePrompt?: string;
  /** For stock footage: choose another clip for the same search. */
  regenerateImage?: boolean;
  stockQuery?: string;
  overrideReason?: string | null;
  [key: string]: unknown;
}

/** A new scene for a microreel; clip reels (cut from a video) take no new scenes. */
export interface AddSceneBody {
  /** The scene the new one follows; null puts it first. */
  afterSceneId: string | null;
  narration: string;
  /** The words shown on screen; every one-line layout needs them. */
  onScreen: string;
  /** The source page the scene is based on: required when the storyboard was made from a document or video. */
  cites?: Citation[] | null;
}

export interface EndingBody {
  question?: Question;
  attestation?: Attestation;
}

export interface PublishBody {
  name?: string;
  versionHash?: string;
  expectedStoreVersion?: number | null;
  expectedPreviousRevisionId?: string | null;
}

interface StudioRequest {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Sent as JSON. */
  body?: unknown;
  /** Sent as multipart form data instead of `body`. */
  form?: FormData;
  signal?: AbortSignal;
  version?: StudioVersion;
  responseType?: 'blob';
}

class StudioApi {
  public async options<T = Record<string, unknown>>(organizationId: string) {
    return this.request<T>(organizationId, '/options');
  }

  /** The voices for a narration language and dialect: Emma and James, then Cartesia's. */
  public async voices(
    organizationId: string,
    query: {language: string; region: string},
    options?: StudioRequestOptions,
  ) {
    return this.request<{voices: LibraryVoice[]}>(
      organizationId,
      `/voices?${new URLSearchParams(query)}`,
      options,
    );
  }

  /** How busy Studio is: about how long a new storyboard or render would wait to start. */
  public async capacity(
    organizationId: string,
    options?: StudioRequestOptions,
  ) {
    return this.request<StudioCapacity>(organizationId, '/capacity', options);
  }

  public async listJobs(
    organizationId: string,
    options?: StudioRequestOptions,
  ) {
    return this.request<JobSummary[]>(organizationId, '/jobs', options);
  }

  public async getJob(
    organizationId: string,
    id: string,
    options?: StudioRequestOptions,
  ) {
    return this.request<JobView>(organizationId, `/jobs/${id}`, options);
  }

  /** Reloads a scene picture through the session-refreshing client after a native image request failed. */
  public async sceneImage(
    organizationId: string,
    id: string,
    kind: 'images' | 'stock',
    asset: string,
    options?: StudioRequestOptions,
  ) {
    return this.request<Blob>(
      organizationId,
      `/jobs/${encodeURIComponent(
        id,
      )}/media/assets/${kind}/${encodeURIComponent(asset)}`,
      {...options, responseType: 'blob'},
    );
  }

  /** The brief (source, settings and course options) as JSON, with the uploaded document. */
  public async createJob(
    organizationId: string,
    brief: unknown,
    file?: File | null,
    documentText?: string,
  ) {
    const form = new FormData();
    form.append('brief', JSON.stringify(brief));
    if (file) form.append('file', file);
    if (documentText) form.append('documentText', documentText);
    return this.request<JobView>(organizationId, '/jobs', {
      method: 'POST',
      form,
    });
  }

  public async renameJob(
    organizationId: string,
    id: string,
    title: string,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${encodeURIComponent(id)}`,
      {method: 'PATCH', body: {title}, version},
    );
  }

  /** Renames one microreel; the video is unchanged, so a current render stays current. */
  public async renameReel(
    organizationId: string,
    id: string,
    reelId: string,
    title: string,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${encodeURIComponent(id)}/reels/${encodeURIComponent(reelId)}`,
      {method: 'PATCH', body: {title}, version},
    );
  }

  public async deleteJob(
    organizationId: string,
    id: string,
    version: StudioVersion,
  ) {
    return this.request<{deleted: boolean}>(
      organizationId,
      `/jobs/${encodeURIComponent(id)}`,
      {method: 'DELETE', version},
    );
  }

  public async replan(
    organizationId: string,
    id: string,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${encodeURIComponent(id)}/replan`,
      {method: 'POST', version},
    );
  }

  public async chooseTopics(
    organizationId: string,
    id: string,
    topics: string[],
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${encodeURIComponent(id)}/topics`,
      {method: 'POST', body: {topics}, version},
    );
  }

  public async addScene(
    organizationId: string,
    id: string,
    reelId: string,
    scene: AddSceneBody,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${id}/reels/${reelId}/scenes`,
      {method: 'POST', body: scene, version},
    );
  }

  public async editScene(
    organizationId: string,
    id: string,
    reelId: string,
    sceneId: string,
    edit: SceneEdit,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${id}/reels/${reelId}/scenes/${sceneId}`,
      {method: 'PATCH', body: edit, version},
    );
  }

  public async deleteScene(
    organizationId: string,
    id: string,
    reelId: string,
    sceneId: string,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${id}/reels/${reelId}/scenes/${sceneId}`,
      {method: 'DELETE', version},
    );
  }

  public async moveScene(
    organizationId: string,
    id: string,
    reelId: string,
    sceneId: string,
    direction: number,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${id}/reels/${reelId}/scenes/${sceneId}/move`,
      {method: 'POST', body: {direction}, version},
    );
  }

  public async rewriteScene(
    organizationId: string,
    id: string,
    reelId: string,
    sceneId: string,
    instruction: string,
    version: StudioVersion,
  ) {
    return this.request<{suggestion: string}>(
      organizationId,
      `/jobs/${id}/reels/${reelId}/scenes/${sceneId}/rewrite`,
      {method: 'POST', body: {instruction}, version},
    );
  }

  public async approveOverride(
    organizationId: string,
    id: string,
    reelId: string,
    sceneId: string,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${id}/reels/${reelId}/scenes/${sceneId}/approve-override`,
      {method: 'POST', version},
    );
  }

  public async resolveSme(
    organizationId: string,
    id: string,
    reelId: string,
    response: string,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${id}/reels/${reelId}/resolve-sme`,
      {method: 'POST', body: {response}, version},
    );
  }

  public async suggestQuestion(
    organizationId: string,
    id: string,
    reelId: string,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${id}/reels/${reelId}/ending/suggest`,
      {method: 'POST', version},
    );
  }

  public async setEnding(
    organizationId: string,
    id: string,
    reelId: string,
    ending: EndingBody,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${id}/reels/${reelId}/ending`,
      {method: 'PUT', body: ending, version},
    );
  }

  /**
   * A new look for a whole microreel: a new design revision. A kept look keeps its family,
   * palette and type, and only its arrangement and movement change. The narration, voice and
   * pictures are kept.
   */
  public async tryAnotherLook(
    organizationId: string,
    id: string,
    reelId: string,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${encodeURIComponent(id)}/reels/${encodeURIComponent(
        reelId,
      )}/motion/look`,
      {method: 'POST', body: {}, version},
    );
  }

  /** Another arrangement and animation for one scene, within its microreel's look. */
  public async tryAnotherAnimation(
    organizationId: string,
    id: string,
    reelId: string,
    sceneId: string,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${encodeURIComponent(id)}/reels/${encodeURIComponent(
        reelId,
      )}/scenes/${encodeURIComponent(sceneId)}/motion/animation`,
      {method: 'POST', body: {}, version},
    );
  }

  /** Keeps (or lets go of) a microreel's look, or changes its motion amount, sound effects or palette. */
  public async updateReelMotion(
    organizationId: string,
    id: string,
    reelId: string,
    change: ReelMotionUpdate,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${encodeURIComponent(id)}/reels/${encodeURIComponent(
        reelId,
      )}/motion`,
      {method: 'PUT', body: change, version},
    );
  }

  public async updateBrandColors(
    organizationId: string,
    id: string,
    colors: Partial<Record<BrandColorRole, string>>,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${encodeURIComponent(id)}/brand/colors`,
      {method: 'PUT', body: {colors}, version},
    );
  }

  /** `fontFamily` is the id of a font GET /options lists. */
  public async updateBrandFont(
    organizationId: string,
    id: string,
    fontFamily: string,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${encodeURIComponent(id)}/brand/font`,
      {method: 'PUT', body: {fontFamily}, version},
    );
  }

  /** The logo must already be a PNG; see `logoToPng`. */
  public async uploadBrandLogo(
    organizationId: string,
    id: string,
    logo: Blob,
    version: StudioVersion,
  ) {
    const form = new FormData();
    form.append('file', logo, 'logo.png');
    return this.request<JobView>(
      organizationId,
      `/jobs/${encodeURIComponent(id)}/brand/logo`,
      {method: 'POST', form, version},
    );
  }

  /** The next render has no logo, even if the document had one. */
  public async removeBrandLogo(
    organizationId: string,
    id: string,
    version: StudioVersion,
  ) {
    return this.request<JobView>(
      organizationId,
      `/jobs/${encodeURIComponent(id)}/brand/logo`,
      {method: 'DELETE', version},
    );
  }

  /** `versionHash` is the storyboard revision the author reviewed. */
  public async render(
    organizationId: string,
    id: string,
    version: StudioVersion,
    versionHash?: string,
  ) {
    return this.request<JobView>(organizationId, `/jobs/${id}/render`, {
      method: 'POST',
      body: {versionHash},
      version,
    });
  }

  public async cancel(
    organizationId: string,
    id: string,
    version: StudioVersion,
  ) {
    return this.request<{cancelled: boolean}>(
      organizationId,
      `/jobs/${id}/cancel`,
      {method: 'POST', version},
    );
  }

  /**
   * Settles the AI credits a stopped generation still holds, e.g. once the workspace has
   * added credits. It changes billing rather than the storyboard, so it sends no version.
   */
  public async creditCheck(organizationId: string, id: string) {
    return this.request<{settled: boolean}>(
      organizationId,
      `/jobs/${id}/credit-check`,
      {method: 'POST'},
    );
  }

  public async publication(organizationId: string, id: string) {
    return this.request<Publication | null>(
      organizationId,
      `/jobs/${id}/publication`,
    );
  }

  public async publish(
    organizationId: string,
    id: string,
    body: PublishBody = {},
  ) {
    return this.request<Publication>(organizationId, `/jobs/${id}/publish`, {
      method: 'POST',
      body,
      version: body.expectedStoreVersion,
    });
  }

  /**
   * Every change is marked as a Studio request (X-Studio-Request) and carries the version it
   * was made against (If-Match).
   */
  private async request<T>(
    organizationId: string,
    path: string,
    {
      method = 'GET',
      body,
      form,
      signal,
      version,
      responseType,
    }: StudioRequest = {},
  ): Promise<StudioApiResponse<T>> {
    return apiClient.request<T, StudioApiResponse<T>>({
      url: `/studio/${organizationId}${path}`,
      method,
      data: form || body,
      headers: {
        ...(body ? {'Content-Type': 'application/json'} : {}),
        ...(method !== 'GET' ? {'X-Studio-Request': '1'} : {}),
        ...(version != null ? {'If-Match': `"${version}"`} : {}),
      },
      signal,
      responseType,
    });
  }
}

export default StudioApi;
